import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Truck, MapPin, Package, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react'
import { useCreateShipment } from '../hooks/use-shipping'

interface CreateShipmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: any | null
  onSuccess?: () => void
}

export function CreateShipmentDialog({
  open,
  onOpenChange,
  order,
  onSuccess,
}: CreateShipmentDialogProps) {
  const createShipmentMutation = useCreateShipment()
  const [weightKg, setWeightKg] = useState<number>(1.5)
  const [lengthCm, setLengthCm] = useState<number>(25)
  const [breadthCm, setBreadthCm] = useState<number>(20)
  const [heightCm, setHeightCm] = useState<number>(15)
  const [ewaybillNo, setEwaybillNo] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [booked, setBooked] = useState<any>(null)

  if (!order) return null

  const shippingAddr: any = order.shippingAddress || {}
  const itemCount = order.items?.reduce((s: number, it: any) => s + it.quantity, 0) || 1
  const isHighValue = Number(order.totalAmount || 0) >= 50000

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      const result = await createShipmentMutation.mutateAsync({
        orderId: order.id,
        payload: {
          weightKg,
          lengthCm,
          breadthCm,
          heightCm,
          ...(ewaybillNo ? { ewaybillNo: ewaybillNo.trim() } : {}),
        },
      })
      setBooked(result)
      if (onSuccess) onSuccess()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create shipment on Bigship. Check credentials in .env')
    }
  }

  const handleClose = () => {
    setBooked(null)
    setError('')
    onOpenChange(false)
  }

  // Success state
  if (booked) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center text-center space-y-4 py-4">
            <div className="size-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="size-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-foreground">Shipment Booked!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Order #{order.orderNumber} dispatched via Bigship
              </p>
            </div>
            <div className="w-full rounded-xl border border-border bg-muted/20 p-4 space-y-2 text-left text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">AWB Number</span>
                <span className="font-mono font-bold text-foreground">{booked.awbNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">Courier Partner</span>
                <span className="font-semibold text-foreground">{booked.courierPartner}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">Status</span>
                <Badge className="bg-blue-500 text-white text-[11px] font-bold">SHIPPED</Badge>
              </div>
            </div>
            <Button className="w-full font-bold bg-primary text-white" onClick={handleClose}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Truck className="size-5" />
            </div>
            <div>
              <DialogTitle className="font-display text-lg font-bold">
                Book Shipment via Bigship
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Creates draft order → gets courier rates → manifests AWB for Order #{order.orderNumber}
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 p-3 text-xs bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-2 text-xs">
          {/* Info note */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg text-[11px] text-blue-600 dark:text-blue-400">
            <Info className="size-3.5 shrink-0 mt-0.5" />
            <span>
              Warehouse & courier will be auto-selected from your Bigship account settings.
              The order will appear live in your Bigship dashboard once booked.
            </span>
          </div>

          {/* Destination Preview */}
          <div className="rounded-xl border border-border bg-muted/20 p-3.5 space-y-1.5">
            <span className="font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 text-[11px]">
              <MapPin className="size-3 text-primary" /> Delivery Destination (Consignee)
            </span>
            <p className="font-bold text-foreground text-sm">
              {shippingAddr.fullName || `${order.user?.firstName || ''} ${order.user?.lastName || ''}`.trim() || 'Customer'}
            </p>
            <p className="text-muted-foreground">
              {shippingAddr.addressLine1}
              {shippingAddr.addressLine2 ? `, ${shippingAddr.addressLine2}` : ''}
            </p>
            <p className="text-muted-foreground font-mono">
              {shippingAddr.city}, {shippingAddr.state} - <strong>{shippingAddr.postalCode}</strong>
            </p>
            <p className="text-muted-foreground">Contact: {shippingAddr.phone || order.user?.phone || 'N/A'}</p>
          </div>

          {/* Order Items Summary */}
          <div className="rounded-xl border border-border bg-muted/10 p-3 space-y-1">
            <span className="font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 text-[11px]">
              <Package className="size-3 text-primary" /> Items in Shipment ({itemCount} {itemCount === 1 ? 'part' : 'parts'})
            </span>
            <div className="space-y-0.5 pt-1">
              {order.items?.slice(0, 4).map((it: any, idx: number) => (
                <div key={idx} className="flex justify-between text-[11px]">
                  <span className="text-foreground font-semibold truncate max-w-[200px]">{it.title}</span>
                  <span className="text-muted-foreground font-mono">x{it.quantity}</span>
                </div>
              ))}
              {(order.items?.length || 0) > 4 && (
                <p className="text-[11px] text-muted-foreground">+{order.items.length - 4} more items...</p>
              )}
            </div>
          </div>

          {/* Package Specs */}
          <div className="space-y-2">
            <span className="font-bold text-foreground uppercase tracking-wider text-[11px] flex items-center gap-1">
              <Package className="size-3 text-primary" /> Package Dimensions & Weight
            </span>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <label className="text-[11px] text-muted-foreground font-semibold">
                  Gross Weight (kg) *
                </label>
                <Input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={weightKg}
                  onChange={(e) => setWeightKg(Number(e.target.value))}
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground font-semibold">Length (cm) *</label>
                <Input type="number" min="1" value={lengthCm} onChange={(e) => setLengthCm(Number(e.target.value))} required />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground font-semibold">Breadth (cm) *</label>
                <Input type="number" min="1" value={breadthCm} onChange={(e) => setBreadthCm(Number(e.target.value))} required />
              </div>

              <div className="space-y-1 col-span-2">
                <label className="text-[11px] text-muted-foreground font-semibold">Height (cm) *</label>
                <Input type="number" min="1" value={heightCm} onChange={(e) => setHeightCm(Number(e.target.value))} required />
              </div>
            </div>
          </div>

          {/* E-way bill for high-value orders */}
          {isHighValue && (
            <div className="space-y-1.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-amber-600 dark:text-amber-400">
                  E-Way Bill Number (Orders &ge; &#8377;50,000)
                </label>
                <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500 font-semibold">
                  Govt / Bigship Requirement
                </Badge>
              </div>
              <Input
                placeholder="Enter 12-digit E-Way Bill Number (e.g. 121456789012)"
                value={ewaybillNo}
                onChange={(e) => setEwaybillNo(e.target.value)}
                className="font-mono text-xs"
              />
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                As per Indian GST regulations, consignments with invoice value exceeding &#8377;50,000 require an active E-way bill for inter/intra-state logistics clearance.
              </p>
            </div>
          )}

          <DialogFooter className="pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createShipmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary text-white font-bold gap-1.5"
              disabled={createShipmentMutation.isPending}
            >
              {createShipmentMutation.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Booking with Bigship...
                </>
              ) : (
                <>
                  <Truck className="size-4" /> Book & Generate AWB
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
